import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import ReactTooltip from 'react-tooltip';

import {
  isEligibleForShutterbugFreeTrial,
  isSearchSubscriptionActiveTrial,
  SEARCH_SUBSCRIPTION_PLAN,
  SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD,
  SEARCH_SUBSCRIPTION_PLAN_PRIORITY,
  searchSubscriptionMeta,
} from '../../../PhotoSearchCheckout/helpers';
import CheckmarkIcon from '../../../../../../../components/icons/CheckmarkIcon';
import Button from '../../../../../../../components/global/Button';
import { formatEnumValue } from '../../../../../../../util/text';
import { formatPriceForDisplay } from '../../../../../../../util/format-money';
import BoxedItem from '../../../../../../../components/global/BoxedItem';
import Badge from '../../../../../../../components/global/Badge';
import { SHUTTERBUG_TRIAL_DAYS_FALLBACK } from '../../helpers';
import CustomControl from '../../../../../../../components/global/CustomControl';
import {
  ALBUM_SUBTYPE,
  ALBUM_TYPE,
} from '../../../../../../../constants/albums';
import InfoCircleIcon from '../../../../../../../components/icons/InfoCircleIcon';

const propTypes = {
  searchSubscriptionItem: PropTypes.shape({
    plan: PropTypes.string,
  }),
  searchSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      plan: PropTypes.string,
    }),
  ),
  selected: PropTypes.bool,
  className: PropTypes.string,
  activeSearchSubscription: PropTypes.shape({
    id: PropTypes.string.isRequired,
    billing_period: PropTypes.string.isRequired,
    payment_processor: PropTypes.string.isRequired,
    plan: PropTypes.string.isRequired,
    cancelled_at: PropTypes.string.isRequired,
    expires_at: PropTypes.string.isRequired,
    trial_days: PropTypes.number,
  }).isRequired,
  onPurchase: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  account: PropTypes.shape({
    id: PropTypes.string,
    default_payment_method: PropTypes.shape({
      last_4: PropTypes.string,
    }),
  }),
  subtitle: PropTypes.node,
  checkoutBtnProps: PropTypes.object,
  checkoutBtnText: PropTypes.string,
  onSelected: PropTypes.func,
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
    album_type: PropTypes.oneOf(Object.keys(ALBUM_TYPE)),
    album_subtype: PropTypes.oneOf(Object.keys(ALBUM_SUBTYPE)),
  }),
};

const SearchSubscriptionPlanOverview = ({
  searchSubscriptionItem,
  searchSubscriptionItems,
  selected,
  className,
  activeSearchSubscription,
  onPurchase,
  submitting,
  subtitle,
  checkoutBtnProps = {},
  checkoutBtnText = '',
  onSelected,
  account,
  albumFolder,
}) => {
  const [hover, setHover] = useState(false);
  const [yearlySub, monthlySub] = searchSubscriptionItems ?? [];
  const [selectedSubscriptionItem, setSelectedSubscriptionItem] = useState(
    searchSubscriptionItem ?? yearlySub,
  );

  useEffect(() => {
    setHover(false);
  }, [activeSearchSubscription?.cancelled_at]);

  const isCancelled = !!activeSearchSubscription?.cancelled_at;

  const isActiveSearchSubscription =
    (!activeSearchSubscription &&
      selectedSubscriptionItem?.plan === SEARCH_SUBSCRIPTION_PLAN.FREE) ||
    (activeSearchSubscription?.plan === selectedSubscriptionItem?.plan &&
      activeSearchSubscription?.billing_period ===
        selectedSubscriptionItem?.billing_period);

  const {
    featuresWithTip,
    featuresShort = [],
    onboardingFeatures,
    badgeText,
    shortLabel,
    shortSubtitle,
  } = searchSubscriptionMeta[selectedSubscriptionItem?.plan];

  const features = useMemo(() => {
    if (albumFolder) {
      return onboardingFeatures(albumFolder);
    }

    if (featuresWithTip) {
      return featuresWithTip;
    }

    return featuresShort;
  }, [albumFolder]);

  const classes = classNames({
    'photo-search-upsell-plan-option-item': true,
    cancellable: isActiveSearchSubscription && !isCancelled,
    selected,
    [className]: !!className,
  });

  const isFree = !selectedSubscriptionItem?.price?.value;
  const isFreePlan =
    selectedSubscriptionItem?.plan === SEARCH_SUBSCRIPTION_PLAN.FREE;

  const [, title] = formatEnumValue(selectedSubscriptionItem.plan).split(' ');
  const planName = title ? `Waldo ${title}` : shortLabel;

  const checkoutButtonLabel = (() => {
    if (isActiveSearchSubscription) {
      if (hover && activeSearchSubscription) {
        return isCancelled ? 'Restore plan' : 'Cancel plan';
      }

      if (
        isSearchSubscriptionActiveTrial(activeSearchSubscription) &&
        isCancelled
      ) {
        return 'Cancelled (Free trial)';
      }

      if (isSearchSubscriptionActiveTrial(activeSearchSubscription)) {
        return 'Active (Free trial)';
      }

      if (isCancelled) {
        return 'Active (Cancelled)';
      }

      return 'Current';
    }

    if (checkoutBtnText) {
      return checkoutBtnText;
    }

    if (isFree) {
      return 'FREE';
    }

    if (activeSearchSubscription?.plan === selectedSubscriptionItem.plan) {
      return 'Update';
    }

    if (
      isEligibleForShutterbugFreeTrial(account) &&
      selectedSubscriptionItem?.trial_days > 0
    ) {
      return `Start ${
        selectedSubscriptionItem?.trial_days ?? SHUTTERBUG_TRIAL_DAYS_FALLBACK
      } day free trial!`;
    }

    return !activeSearchSubscription ||
      SEARCH_SUBSCRIPTION_PLAN_PRIORITY[selectedSubscriptionItem.plan] >
        SEARCH_SUBSCRIPTION_PLAN_PRIORITY[activeSearchSubscription.plan]
      ? `Activate ${planName} ${
          selectedSubscriptionItem?.billing_period ===
          SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY
            ? 'Yearly'
            : 'Monthly'
        }`
      : 'Downgrade';
  })();

  const subtitleLabel = (() => {
    if (activeSearchSubscription && isActiveSearchSubscription) {
      return (
        <>
          <p>
            {isCancelled ? 'Expires at: ' : 'Next billing at: '}
            <span className="text-primary">
              {moment(activeSearchSubscription.expires_at).format('MM/DD/YYYY')}
            </span>
          </p>
        </>
      );
    }

    return subtitle;
  })();

  return (
    <BoxedItem
      className={classes}
      onClick={() => onSelected(selectedSubscriptionItem)}
    >
      <div className="photo-search-upsell-plan-option-item-header">
        {badgeText && (
          <Badge badgeStyle="yellow" badgeSize="lg">
            {badgeText}
          </Badge>
        )}
        <p className="photo-search-upsell-plan-option-item-title">
          {planName}
          <span className="photo-search-upsell-plan-option-item-title-subtitle">
            {shortSubtitle}
          </span>
        </p>
      </div>
      <div className="photo-search-upsell-plan-option-item-content">
        <ul>
          {features.map(feature => {
            return (
              <li
                key={feature?.id ?? feature?.label}
                className="text-dark text-bold align-items-center"
              >
                <Fragment>
                  {!feature?.keyless && (
                    <CheckmarkIcon
                      color="#f1794e"
                      size="14px"
                      className="feature-icon"
                    />
                  )}
                  {typeof feature === 'string' ? (
                    <span>{feature}</span>
                  ) : (
                    <span>
                      <span>{feature.value}</span>
                      {feature.tip && (
                        <Fragment>
                          <span
                            className="ml-1"
                            data-for={`tooltip-${feature.id}`}
                            data-tip=""
                            data-event="click"
                            data-effect="solid"
                            data-place="right"
                          >
                            <InfoCircleIcon size={12} />

                            <ReactTooltip
                              id={`tooltip-${feature.id}`}
                              className="tooltip"
                              globalEventOff="click"
                            >
                              {feature.tip}
                            </ReactTooltip>
                          </span>
                        </Fragment>
                      )}
                    </span>
                  )}
                </Fragment>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="photo-search-upsell-plan-option-item-footer">
        {!isFreePlan && monthlySub && yearlySub && (
          <div className="text-primary text-sm text-normal mb-1">
            <CustomControl
              type="radio"
              size="sm"
              className="d-block"
              checked={
                selectedSubscriptionItem?.billing_period ===
                SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.YEARLY
              }
              label={
                <>
                  <span className="text-extra-bold">
                    ${formatPriceForDisplay(yearlySub.price.value)}
                  </span>{' '}
                  / year (save 48%!)
                </>
              }
              onChange={() => {
                setSelectedSubscriptionItem(yearlySub);
              }}
            />
            <CustomControl
              type="radio"
              size="sm"
              className="d-block"
              checked={
                selectedSubscriptionItem?.billing_period ===
                SEARCH_SUBSCRIPTION_PLAN_BILLING_PERIOD.MONTHLY
              }
              label={
                <>
                  <span className="text-extra-bold">
                    ${formatPriceForDisplay(monthlySub.price.value)}
                  </span>{' '}
                  / month
                </>
              }
              onChange={() => {
                setSelectedSubscriptionItem(monthlySub);
              }}
            />
          </div>
        )}
        <Button
          onClick={() => {
            onPurchase({ searchSubscriptionItem: selectedSubscriptionItem });
          }}
          btnBlock
          btnSize="sm"
          disabled={submitting}
          btnStyle={(() => {
            if (isActiveSearchSubscription) {
              if (hover && activeSearchSubscription) {
                return isCancelled ? 'primary' : 'danger';
              }

              return 'active-disabled';
            }

            return 'primary';
          })()}
          btnOutline={isActiveSearchSubscription && !isCancelled && hover}
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
          {...checkoutBtnProps}
        >
          <Fragment>{checkoutButtonLabel}</Fragment>
        </Button>
      </div>
      {subtitleLabel && (
        <div className="photo-search-upsell-plan-option-item-subtitle">
          {subtitleLabel}
        </div>
      )}
      {isActiveSearchSubscription && !isFreePlan && (
        <>
          {!isCancelled && (
            <Button
              btnStyle="link"
              btnSize="sm"
              className="text-danger mb-2"
              onClick={() =>
                onPurchase({ searchSubscriptionItem: selectedSubscriptionItem })
              }
            >
              Downgrade to Basic (Free)
            </Button>
          )}
          {isCancelled && (
            <p className="photo-search-upsell-plan-option-cancel-text">
              Plan cancelled.
              <br />
              Your plan expires at
              <br />
              <span className="text-bold">
                {moment(activeSearchSubscription.expires_at).format(
                  'MM/DD/YYYY',
                )}
              </span>
            </p>
          )}
        </>
      )}
    </BoxedItem>
  );
};

SearchSubscriptionPlanOverview.propTypes = propTypes;

export default SearchSubscriptionPlanOverview;
